"use client";

import { usePathname } from "next/navigation";
import { useNavigation } from "@/app/hooks/useNavigation";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";

import { ButtonLink } from "@/components/atoms/Link/link";
import { HeadingText } from "@/components/atoms/HeadingText/heading-text";
import Text from "@/components/atoms/Text/text";
import Rating from "@/components/molecules/Rating/rating";
import HeroCarousel from "@/components/organisms/Carousel/HeroCarousel";
import { promoSlides, type PromoSlide } from "@/app/constants/promo-slides";

interface HeroTranslations {
  products: string;
  sellers: string;
  activeUsers: string;
  title: string;
  subtitle: string;
  description: string;
  startSelling: string;
  listProduct: string;
  verifiedSellers: string;
  satisfactionRate: string;
  trending: string;
  featured: string;
  viewProfile: string;
  thisWeek: string;
  activeBusinesses: string;
}

// Use shared PromoSlide type from constants

interface HeroSectionProps {
  translations: HeroTranslations;
  slides?: PromoSlide[];
}

// Enhanced background renderer with priority support for LCP
export const renderSlide = (slide: PromoSlide | { image: string }, index: number, priority: boolean = false) => (
  <div key={index} className="relative w-full md:h-[450px] h-[510px]">
    <Image
      src={slide.image}
      alt="Hero background"
      fill
      priority={priority}
      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      sizes="100vw"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-gray-900/60 to-gray-800/40 z-10" />
    <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent z-10" />
  </div>
);

const HeroSection = ({ translations, slides = promoSlides }: HeroSectionProps) => {
  const pathname = usePathname();
  const { handleNavigation } = useNavigation();
  const isHome = pathname === "/";
  const href = isHome ? "#services" : "/#services";
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track slide change with smooth transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % promoSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const currentBiz = slides[activeIndex] || promoSlides[0];



  return (
    <>
      {/* Desktop & Tablet Version (lg and above) */}
      <section className="relative w-full overflow-hidden hidden lg:block">
        <HeroCarousel
          slides={slides}
          renderSlide={(slide, index) => renderSlide(slide as any, index, activeIndex === index)}
          contentPosition="static-overlay"
          autoPlay={true}
          interval={5000}
          staticContent={
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="mx-auto max-w-screen-2xl w-full px-4 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

                  {/* Left Content - Animated entrance */}
                  <div className="lg:col-span-7 space-y-6 lg:space-y-8 animate-fadeInUp">
                    <div className="max-w-2xl">
                      <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100/10 backdrop-blur-sm border border-blue-200/20 mb-4 animate-pulse-slow">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-ping"></span>
                        <Text className="text-sm !text-blue-300 font-medium w-full" >
                          + {translations.activeBusinesses}
                        </Text>
                      </div>

                      <HeadingText
                        level={1}
                        className="text-3xl lg:text-5xl font-bold leading-tight !text-gray-50 animate-slideInUp"
                      >
                        {translations.title}
                      </HeadingText>

                      <Text className="text-xl lg:text-2xl !text-blue-200 font-light mt-4 animate-slideInUp delay-200">
                        {translations.subtitle}
                      </Text>

                      <Text className="mt-6 text-lg !text-gray-300 leading-relaxed animate-slideInUp delay-300">
                        {translations.description}
                      </Text>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 animate-slideInUp delay-400">
                      <ButtonLink
                        href="/dashboard"
                        className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white hover:from-blue-700 hover:to-purple-700 border-none transform hover:scale-105 transition-all duration-300 shadow-2xl"
                      >
                        <span className="relative z-10">{translations.startSelling}</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                      </ButtonLink>

                      <ButtonLink
                        href={href}
                        className="rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur-sm px-8 py-4 text-lg font-semibold text-white hover:bg-white/20 hover:border-white/40 transform hover:scale-105 transition-all duration-300 shadow-xl"
                      >
                        {translations.listProduct}
                      </ButtonLink>
                    </div>
                  </div>

                  {/* Right Business Cards - Enhanced animations */}
                  <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
                    <div
                      ref={cardRef}
                      className={`relative transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                    >
                      {/* Floating Cards Effect */}
                      <div className="relative">
                        {/* Main Card */}
                        <div
                          key={activeIndex}
                          className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700 max-w-md transform hover:scale-105 transition-transform duration-500 group cursor-pointer"
                        >
                          {/* Glow Effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          <div className="relative z-10">
                            {/* Company Header */}
                            <div className="flex items-center space-x-4 mb-6">
                              <div className="relative">
                                <Image
                                  src={currentBiz.logo}
                                  alt={currentBiz.name}
                                  width={80}
                                  height={80}
                                  className="rounded-2xl object-cover shadow-lg group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute -bottom-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                                  {currentBiz.rating} ★
                                </div>
                              </div>
                              <div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 transition-colors">
                                  {currentBiz.name}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                  {currentBiz.category}
                                </p>
                                <div className="flex items-center mt-1">
                                  <span className="text-yellow-500 text-sm">{"★".repeat(5)}</span>
                                  <span className="text-gray-500 text-sm ml-2">({currentBiz.reviews})</span>
                                </div>
                              </div>
                            </div>

                            {/* Description */}
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6 line-clamp-3">
                              {currentBiz.description}
                            </p>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                              <button onClick={() => handleNavigation(currentBiz.website)} className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300 transform hover:scale-105">
                                {currentBiz.ctaText || translations.viewProfile}
                              </button>
                              <button onClick={() => handleNavigation(currentBiz.phone)} className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300">
                                ⋯
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Floating mini cards */}
                        <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg transform rotate-6 animate-float">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg" />
                            <div>
                              <div className="text-sm font-semibold">{translations.trending}</div>
                              <div className="text-xs text-gray-500">+24 {translations.thisWeek}</div>
                            </div>
                          </div>
                        </div>

                        <div className="absolute -top-6 -right-6 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg transform -rotate-6 animate-float delay-1000">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">#{activeIndex + 1}</div>
                            <div className="text-xs text-gray-500">{translations.featured}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </section>

      {/* Mobile Version (lg and below) */}
      <section className="relative w-full overflow-hidden lg:hidden">
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 pt-8 pb-6">
          {/* Hero Content */}
          <div className="px-6">
            <div className="text-center space-y-6">
              {/* Main Text */}
              <div className="space-y-4">
                <HeadingText
                  level={1}
                  className="text-3xl font-bold !text-white leading-tight"
                >
                  {translations.title}
                </HeadingText>

                <Text className="text-lg !text-gray-200 leading-relaxed">
                  {translations.description}
                </Text>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <ButtonLink
                  href="/dashboard"
                  className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-lg font-semibold text-white hover:from-blue-700 hover:to-purple-700 border-none"
                >
                  {translations.startSelling}
                </ButtonLink>

                <ButtonLink
                  href={href}
                  className="rounded-2xl border-2 border-white/30 bg-white/10 px-6 py-4 text-lg font-semibold text-white hover:bg-white/20"
                >
                  {translations.listProduct}
                </ButtonLink>
              </div>
            </div>
          </div>

          {/* Featured Business Header */}
          <div className="px-6 my-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">
                {translations.featured}
              </h2>
              <span className="text-blue-300 text-sm">
                {activeIndex + 1}/{slides.length}
              </span>
            </div>
          </div>

          {/* Mobile Business Cards */}
          <div className="px-6 mb-4">
            <div className="relative">
              {/* Main Business Card */}
              <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-3xl p-6 shadow-2xl border border-white/20 mx-auto">
                {/* Business Header */}
                <div className="flex items-center space-x-4 mb-4">
                  <Image
                    src={currentBiz.logo}
                    alt={currentBiz.name}
                    width={70}
                    height={70}
                    className="rounded-2xl shadow-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-gray-100 text-xl">
                      {currentBiz.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">
                        {currentBiz.category}
                      </span>
                      {/* Ratings and Views */}
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 backdrop-blur-md break-words flex-wrap">
                          <Rating stars={currentBiz.rating || 4.5} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed text-sm">
                  {currentBiz.description}
                </p>

                {/* Action Button */}
                <button className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors" onClick={() => handleNavigation(currentBiz.website)}>
                  {currentBiz.ctaText || translations.viewProfile}
                </button>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center space-x-2 mt-6">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${index === activeIndex ? 'bg-white' : 'bg-white/30'
                      }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;